/**
 * What the screens and the server say to each other. One websocket per screen; every message is
 * JSON. The server is the only writer of the event log; a screen asks for an append and gets an
 * ack with the sequence number, or a refusal with the reason in plain words.
 *
 * Links: /p/<token> projector, /p/<token>/cal calibration, /c/<token>/<n> court n,
 * /a/<token> admin (the owner's link, or a named admin's), /j/<token> players. Websocket: /ws/<role>/<token>[/<court>]?t=<tournament>.
 * The watch remote has no screen and no websocket: /api/remote/<token> over plain HTTPS (remote.ts).
 */

import type { LoggedEvent, TournamentEvent, TournamentState } from "../engine/types";
import type { PinBook, PinKind, PinMode } from "./pins";

/**
 * watcher: sees everything an admin sees and can change nothing (the laptop that keeps an eye on the day).
 * remote: a wrist watch that reads a small summary and presses four buttons (pause, one more minute,
 * finish the round, start the next).
 */
export type Role = "projector" | "court" | "admin" | "player" | "watcher" | "remote";

/** Which screens are connected right now, and when each kind was last seen leaving. */
export interface Screens {
  projector: number;
  /** One count per court, court 1 first. */
  courts: number[];
  admin: number;
  players: number;
  watcher: number;
  /** ms since the epoch a screen of that kind last dropped: "projector", "court-2", "watcher". */
  gone: Record<string, number>;
}

export interface Identity {
  role: Role;
  /** 1-based, for the court role only. */
  court?: number;
  tournament: string;
  /** Admins only: true on the owner's link, false on a named admin's (decision 38). */
  owner?: boolean;
  /** A named admin's name, which their actions are logged under. */
  name?: string;
  /**
   * Players only: the player this phone got in as with their PIN (decision 39). A player's phone
   * without one can watch, and can do nothing until it has typed the PIN.
   */
  player?: string;
  /** Admins only: true until this phone has typed its admin PIN (decision 40). Nothing secret is sent until then. */
  locked?: boolean;
  /** Admins only: whether this admin has set an admin PIN yet ("Set your admin PIN" or "Admin PIN"). */
  pinSet?: boolean;
}

/** A named admin on the hidden way in: who can be picked (only those who have set a PIN, never the owner). */
export interface AdminName {
  id: string;
  name: string;
}

/**
 * The answer to one PIN request, matched by its `rid`. `reason` in words when it did not work;
 * `set` and `lockedUntil` tell the phone which step to show.
 */
export interface Answer {
  kind: "answer";
  rid: string;
  ok: boolean;
  reason?: string;
  set?: boolean;
  lockedUntil?: number | null;
  /** admin.names: the named admins who have set a PIN, and when the way in opens again if it is paused. */
  admins?: AdminName[];
  pausedUntil?: number | null;
  /** admin.open: the admin link's token to go to (its session came just before, as a session message). */
  token?: string;
  /** admin.open by a kept link: that link has stopped (its admin was removed or renewed), so the phone forgets it. */
  stopped?: boolean;
}

/** Every link, as the admin screen shows them. Court links are one per court. */
export interface Links {
  projector: string;
  calibration: string;
  courts: string[];
  admin: string;
  players: string;
  /** The read-only link for the watcher. Empty for everyone but an admin. */
  watcher: string;
  /** The address the watch remote reads its summary from. Empty for everyone but an admin. */
  remote: string;
  /** The named admins' own links, for every admin. `admin` above is the owner's, and only the owner is sent it. */
  admins: { id: string; name: string; link: string; addedBy?: string }[];
}

export type ClientMessage =
  /**
   * Sent once after connecting. since = the last sequence number the screen holds (0 = none).
   * session: what this phone was given after its PIN, kept per tournament (a player's claims the
   * player again, an admin's unlocks the admin screen). setupKey: the tools on the owner link get
   * in with the setup key instead of a PIN (decision 40).
   */
  | { kind: "hello"; since: number; session?: string; setupKey?: string }
  /** Append an event. id is the screen's idempotency key: the same id is never applied twice. */
  | { kind: "append"; id: string; event: TournamentEvent; by: string }
  /** Admin only: rotate a role's token (all courts at once for "court"). Only the owner may rotate "admin", the owner's link. */
  | { kind: "regenerate"; role: Role }
  /** Admin only: give someone an admin link of their own, with their name on it. */
  | { kind: "admins.add"; name: string }
  /** Admin only: take a named admin's link away. */
  | { kind: "admins.remove"; id: string }
  /** Admin only: a new link for one named admin; their old one stops. */
  | { kind: "admins.renew"; id: string }
  /** Admin only: ask for the links. */
  | { kind: "links" }
  /** Admin only: wipe the log and start the tournament again. confirm must be the word RESET. */
  | { kind: "reset"; confirm: string }
  /** Admin and watcher: the last few events of the log, for "who did what". */
  | { kind: "log"; last: number }
  | { kind: "ping"; t: number }
  // ---- PINs (decisions 39 and 40). Each request carries an id of the phone's own, `rid`, and is answered once with it.
  /** Does this player have a PIN yet, and is their name locked? */
  | { kind: "pin.check"; rid: string; player: string }
  /** A player's phone sets or types the PIN for the name it picked; the right one makes this phone that player. */
  | { kind: "pin.claim"; rid: string; player: string; pin: string; mode: PinMode }
  /** A player's phone forgets who it is ("Not you?"): its session ends. */
  | { kind: "session.end" }
  /** An admin's phone sets or types its admin PIN; the right one unlocks the admin screen. */
  | { kind: "admin.pin"; rid: string; pin: string; mode: PinMode }
  /** The hidden way in, from a player's phone: the named admins who have set a PIN. */
  | { kind: "admin.names"; rid: string }
  /**
   * The hidden way in: an admin PIN, for a named admin picked from the list (`admin`) or for the
   * admin link this phone opened before (`link`). The right one answers with that admin link.
   */
  | { kind: "admin.open"; rid: string; pin: string; admin?: string; link?: string }
  /** Admin only: reset a player's or an admin's PIN. Every phone on it is logged out. */
  | { kind: "pin.reset"; rid: string; as: PinKind; id: string }
  /**
   * The owner's Note key (v26.10.027, a temporary test aid; shared/feedback.ts): the owner's phone
   * asks the wall to describe itself and send its picture for the note `rid`. Never in the log.
   */
  | { kind: "feedback.ask"; rid: string };

export type ServerMessage =
  /**
   * The reply to hello: who you are, the clock, and either the whole state or the events you missed.
   * session: "ok" when the session the hello carried is good, "gone" when it is not any more (a reset PIN).
   */
  | { kind: "welcome"; identity: Identity; serverNow: number; state: TournamentState; session?: "ok" | "gone" }
  | { kind: "events"; events: LoggedEvent[]; serverNow: number }
  | { kind: "ack"; id: string; seq: number; serverNow: number }
  | { kind: "refused"; id: string; reason: string; serverNow: number }
  | { kind: "links"; links: Links }
  | { kind: "log"; events: LoggedEvent[] }
  /** To admins and the watcher: who is connected, whenever that changes. */
  | { kind: "screens"; screens: Screens; serverNow: number }
  | { kind: "pong"; t: number; serverNow: number }
  | { kind: "error"; reason: string }
  /** Who this screen is now, after a PIN or a reset changed it. */
  | { kind: "identity"; identity: Identity }
  /** A new session for this phone to keep for this tournament: a player's, or an admin's with its link's token. */
  | { kind: "session"; as: PinKind; session: string; player?: string; token?: string; name?: string | null; owner?: boolean }
  /** This phone's session has stopped (its PIN was reset, or the day started again): say why, ask again. */
  | { kind: "signed.out"; as: PinKind; reason: string }
  /** To unlocked admin screens: every name's PIN, whenever it changes, and `news` when there is something to tell. */
  | { kind: "pins"; book: PinBook; news?: string }
  /** To the projector: describe what you show and send your picture to /api/feedback/wall/<rid> (the owner's Note key). */
  | { kind: "feedback.snap"; rid: string }
  /** To the owner's admin screens: what the wall said it was showing, for the note `rid`. */
  | { kind: "feedback.wall"; rid: string; text: string }
  | Answer;

/** What each role may append. The server enforces this; the screens only hide what is not allowed. */
export function allowed(identity: Identity, event: TournamentEvent): string | null {
  switch (identity.role) {
    case "admin":
      // a new day only comes from the server's own reset, which appends it without asking here
      if (event.type === "tournament.created") return "Use Reset the day on the More tab";
      // the slides before round 1 are moved from the owner's phone only (named admins are owner: false)
      if (event.type === "slides.set" && identity.owner === false) return "Only the owner's phone moves the slides";
      // the awards and the podium too (decision 53, his ask: "let me as owner control and trigger things")
      if ((event.type === "reveal" || event.type === "reveal.step") && identity.owner === false) {
        return "Only Abhi's phone runs the awards and the podium";
      }
      return null;
    case "watcher":
      return "the watcher only watches";
    case "remote": {
      // A lost watch must not be able to wreck the day: four buttons, each in one exact shape.
      const no = "the watch remote can only pause, add a minute, finish a round or start the next";
      switch (event.type) {
        case "pause.all":
          return typeof event.on === "boolean" && Object.keys(event).every((k) => k === "type" || k === "on") ? null : no;
        case "clock.extended":
          return event.ms === 60_000 ? null : "the watch remote adds one minute at a time";
        case "round.finished":
        case "round.started":
          return null;
        default:
          return no;
      }
    }
    case "projector": {
      // The one thing the projector may set: the look, from its calibration screen on the wall.
      const onlyLook = event.type === "config.updated" && Object.keys(event.patch).every((k) => k === "look");
      return onlyLook ? null : "the projector only watches";
    }
    case "court": {
      if (event.type !== "game.scored") return "a court tablet can only enter a final score";
      if (event.court !== identity.court) return `this tablet is court ${identity.court}`;
      return null;
    }
    case "player": {
      // a player's phone acts only for the player it got in as with their PIN (decision 39)
      if (!identity.player) return "Pick your name and type your PIN first";
      switch (event.type) {
        case "player.status":
          if (event.status !== "in" && event.status !== "break" && event.status !== "left") {
            return "players can mark themselves here, on break, or leaving";
          }
          break;
        case "player.profile":
          // the picture goes through the upload, and only an admin waves someone through
          if (event.photo !== undefined || event.photoWaived !== undefined) return "pictures are sent from the camera step";
          break;
        case "heckle.sent":
        case "curse.thrown":
        case "pick.made":
        case "player.dubbed":
          break;
        default:
          return "players can change their own status and profile, and use the bench";
      }
      return namesOnlyMe(event, identity.player) ? null : "That is not you";
    }
    default:
      return "unknown role";
  }
}

/**
 * Whether an event from a player's phone is only about that player. Status and profile are about
 * the player in `id` (the field the engine reads), the rest about `player`; that field must be
 * this player, and neither field may name anyone else, so an extra `player` beside someone else's
 * `id` changes nothing. `target` (a nickname given to someone) is not looked at here.
 */
function namesOnlyMe(event: TournamentEvent, me: string): boolean {
  if (!event || typeof event !== "object") return false;
  const fields = event as unknown as Record<string, unknown>;
  for (const k of ["player", "id"]) {
    if (k in fields && fields[k] !== me) return false;
  }
  const about = event.type === "player.status" || event.type === "player.profile" ? "id" : "player";
  return fields[about] === me;
}

/** The longest label a screen may put on its own actions in the log. */
const SIGNED_MAX = 40;

/**
 * Who an action is logged as. The server decides from the link the screen came in on, so no
 * phone can put its action under someone else's name: a court tablet is its court, a player's
 * phone is the player the action is about, and a named admin is their own name (decision 38).
 * Only the owner's link signs with what the phone sends, the name typed on it, and even then
 * only as an admin.
 */
export function signedBy(identity: Identity, sent: unknown, event: TournamentEvent): string {
  switch (identity.role) {
    case "admin": {
      if (identity.owner === false) return `admin:${identity.name ?? ""}`;
      const label = typeof sent === "string" ? sent.slice(0, SIGNED_MAX) : "";
      return label === "admin" || label.startsWith("admin:") ? label : "admin";
    }
    case "court":
      return `court-${identity.court ?? 0}`;
    case "player": {
      // the player this phone got in as; before PINs, the player the action is about
      if (identity.player) return `player:${identity.player}`;
      // every event a player may send names its player: `player`, or `id` for status and profile
      const id = !event || typeof event !== "object" ? null : "player" in event ? event.player : "id" in event ? event.id : null;
      return typeof id === "string" && id ? `player:${id}` : "player";
    }
    default:
      return identity.role;
  }
}
